import fetch from 'isomorphic-fetch';
import logger from '../logger';
// import track from './trackers';
import publicEnv from '../publicEnv';
import { getOwnerID, getOwnerJWT } from './DryCookies';
import { refreshSessionViaFirebase } from './firebaseAuth';
import interactionLedger from './interactionLedger';

const {
  API_BASE,
} = publicEnv;

/**
 * Enhanced error detection for database/network failures
 */
const detectErrorType = (error, response) => {
  if (!response) {
    // Network error - no response received
    if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
      return 'networkError';
    }
    if (error.message?.includes('timeout') || error.name === 'TimeoutError') {
      return 'timeoutError';
    }
    return 'connectionError';
  }

  // HTTP error responses
  if (response.status === 503) {
    return 'serviceUnavailable';
  }
  if (response.status === 504) {
    return 'gatewayTimeout';
  }
  if (response.status >= 500) {
    return 'serverError';
  }
  if (response.status === 404) {
    return 'notFound';
  }
  if (response.status === 401 || response.status === 403) {
    return 'authenticationError';
  }

  return 'httpError';
};

/**
 * Get user-friendly error message based on error type
 */
const getErrorMessage = (errorType, status, path) => {
  const errorMessages = {
    networkError: 'Network connection failed. Please check your internet connection.',
    connectionError: 'Failed to connect to server. The database may be unavailable.',
    timeoutError: 'Request timed out. The server may be slow or unavailable.',
    serverError: `Server error (${status}). The database may have lost connection.`,
    serviceUnavailable: 'Service temporarily unavailable. The database may be down for maintenance.',
    gatewayTimeout: 'Gateway timeout. The database connection may have been lost.',
    databaseError: 'Database connection lost. Please try again later.',
    httpError: `HTTP error (${status}) when calling ${path}`,
    authenticationError: 'Authentication failed. Please log in again.',
    notFound: `Resource not found: ${path}`,
  };

  return errorMessages[errorType] || `API call failed: ${path}`;
};

export const dryFetch = async requestInfo => {
  const {
    name = 'UNKNOWN',
    path = '/ver.json',
    options = {},
    timeout = 15000,
  } = requestInfo;
  
  const ownerID = getOwnerID();
  const requestPath = `${API_BASE}${path}`;
  let response = null;

  try {
    const ownerJWT = await getOwnerJWT();
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${ownerJWT}`);
    myHeaders.append('Content-Type', 'application/json');
    
    // Handle null/undefined options (some callers pass null)
    const safeOptions = options || {};
    
    // Merge headers from options if provided, but ensure Authorization is always set
    const headers = safeOptions.headers ? new Headers(safeOptions.headers) : myHeaders;
    if (safeOptions.headers) {
      // If options has headers, merge them but ensure Authorization is preserved
      headers.set('Authorization', `Bearer ${ownerJWT}`);
      headers.set('Content-Type', 'application/json');
    }
    
    // Extract options without headers to avoid duplication
    const { headers: _, ...restOptions } = safeOptions;

    const controller = new AbortController();
    const timeoutId = timeout > 0 ? setTimeout(() => controller.abort(), timeout) : null;

    try {
      response = await fetch(requestPath, {
        method: 'GET',
        headers: headers,
        mode: 'cors',
        signal: controller.signal,
        ...restOptions,
      });
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }

    if (!response.ok) {
      const errorType = detectErrorType(null, response);

      // Silent re-auth retry on 401/403 — exchange Firebase ID token for a fresh
      // dry_jwt via GET /sessionToken, then retry the original request once.
      // Using refreshSessionViaFirebase() instead of validateFirebaseAuth() because
      // validateFirebaseAuth only refreshes the Firebase token, not the dry_jwt cookie
      // the API uses for auth — so the retry would fail with the same expired JWT.
      if (errorType === 'authenticationError' && !requestInfo._retried) {
        logger.warn(`dryFetch: ${response.status} on ${name}, attempting silent session refresh`);
        try {
          const freshJWT = await refreshSessionViaFirebase();
          if (freshJWT) {
            const retryHeaders = new Headers();
            retryHeaders.append('Authorization', `Bearer ${freshJWT}`);
            retryHeaders.append('Content-Type', 'application/json');
            const { headers: _, ...restRetryOptions } = safeOptions;
            const retryResponse = await fetch(requestPath, {
              method: 'GET',
              headers: retryHeaders,
              mode: 'cors',
              ...restRetryOptions,
            });
            if (retryResponse.ok) {
              logger.info(`dryFetch: Retry after session refresh succeeded for ${name}`);
              // Defer event so the current save completes before the flush runs.
              if (typeof window !== 'undefined') {
                setTimeout(() => window.dispatchEvent(new CustomEvent('dry:auth-refreshed')), 0);
              }
              return retryResponse.json();
            }
            logger.warn(`dryFetch: Retry after session refresh also failed (${retryResponse.status}) on ${name}`);
          } else {
            logger.warn(`dryFetch: Session refresh returned no JWT for ${name} — will queue for later retry`);
          }
        } catch (retryError) {
          logger.error('dryFetch: Session refresh retry threw:', retryError);
        }
      }

      const errorMessage = getErrorMessage(errorType, response.status, path);
      const error = new Error(errorMessage);
      error.status = response.status;
      error.errorType = errorType;
      error.path = path;
      error.name = name;

      // Log to interaction ledger
      interactionLedger.log(
        'apiError',
        'api',
        {
          name,
          path,
          requestPath,
          status: response.status,
          statusText: response.statusText,
          errorType,
          ownerID,
          method: (options || {}).method || 'GET',
        },
        `API Error: ${name} - ${errorMessage}`
      );

      logger.error({
        name,
        path,
        status: response.status,
        statusText: response.statusText,
        errorType,
      }, `API call failed: ${name}`);

      throw error;
    }

    // Success - parse and return JSON
    try {
      const data = await response.json();
      return data;
    } catch (parseError) {
      // JSON parsing error
      const error = new Error(`Failed to parse response from ${path}`);
      error.errorType = 'parseError';
      error.path = path;
      error.name = name;

      interactionLedger.log(
        'apiError',
        'api',
        {
          name,
          path,
          requestPath,
          errorType: 'parseError',
          ownerID,
          parseError: parseError.message,
        },
        `API Parse Error: ${name} - Failed to parse response`
      );

      logger.error({
        name,
        path,
        parseError: parseError.message,
      }, `Failed to parse response from ${name}`);

      throw error;
    }
  } catch (error) {
    // Handle network errors, timeouts, and other fetch failures
    if (!response) {
      const errorType = detectErrorType(error, null);
      const errorMessage = getErrorMessage(errorType, null, path);
      
      // Enhance error object
      error.errorType = errorType;
      error.path = path;
      error.name = name;
      if (!error.message || error.message === error.toString()) {
        error.message = errorMessage;
      }

      // Log to interaction ledger
      interactionLedger.log(
        'apiError',
        'api',
        {
          name,
          path,
          requestPath,
          errorType,
          ownerID,
          method: (options || {}).method || 'GET',
          errorMessage: error.message,
          errorName: error.name,
          errorStack: error.stack,
        },
        `API Connection Error: ${name} - ${errorMessage}`
      );

      logger.error({
        name,
        path,
        errorType,
        errorMessage: error.message,
        errorName: error.name,
      }, `API connection failed: ${name}`);
    }

    // Re-throw the error so callers can handle it
    throw error;
  }
};
